import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import type { AggregatedNoteDto } from "../../../services/vorgangNoteService";
import type { MailLinkDto } from "../../../services/mailLinkService";
import { IcoChevronUnten } from "../v3/vg3Icons";
import type { VerlaufEintrag } from "../v3/verlauf";
import { EreignisListeV4, type ZeilenKommentare } from "./EreignisListeV4";
import { archivKopfText, archivZeilen, type ArchivArt } from "./archivZeilen";
import { zipDateiname } from "./dokumenteHerunterladen";
import "./vorgangV4.css";

/**
 * DAS ARCHIV am Seitenende (PO 11.09.2026).
 *
 * ══ EINE RÜCKNAHME ═════════════════════════════════════════════════════════
 * V4 hatte das Archiv gestrichen und die vollständigen Listen hinter „Alle
 * Mails" und „Alle Dokumente" gelegt. Der PO hat das am 11.09.2026
 * zurückgewiesen: „Das muss unten runter, unter alle To-dos, und da ist alles
 * von allen To-dos drin." (17:43) — ein aufklappbarer Abschnitt UNTER allen
 * To-dos, mit einem Suchfeld, das nur in DIESEM Vorgang sucht.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Es ist weder der Verlauf noch das Kundentagebuch (10:40–11:04): der
 * Notizstrom oben bleibt unberührt, hier wird nur nachgeschlagen. Notizen
 * stehen deshalb BEIDES MAL — im Strom und im Archiv; das ist gewollt: „wenn
 * ich danach suchen will, lass sie ruhig anzeigen … schadet nicht." (18:03).
 *
 * Nachschlagen darf, wer die Seite sehen darf.
 *
 * ══ ES BLEIBT ZU (PO 18.09.2026) ═══════════════════════════════════════════
 * „Archiv bleibt bei Vorgang ohne und mit To-dos eingeklappt."
 *
 * Vom 15. bis 18.09.2026 stand es beim Vorgang OHNE To-do aufgeklappt da: er
 * hatte damals keinen eigenen Verlauf, seine Mails waren nur hier zu finden.
 * Seit der Verlauf in JEDER Karte steht — auch in der des Vorgangs ohne To-do
 * —, ist das Archiv wieder das, was es sein soll: die Suche über alles, nicht
 * der zweite Leseort. Wer es aufklappt, bekommt das gemerkt (`sessionStorage`).
 *
 * Die Herkunftsspalte entfällt ohne To-dos weiter (`mitHerkunft`) — sie wäre
 * immer leer.
 * ═══════════════════════════════════════════════════════════════════════════
 */

const ARTEN: Array<{ art: ArchivArt | "alle"; label: string }> = [
    { art: "alle", label: "Alle" },
    { art: "mail", label: "Mails" },
    { art: "anruf", label: "Anrufe" },
    { art: "dokument", label: "Dokumente" },
    { art: "notiz", label: "Notizen" },
];

/**
 * Die Chips „Termine" und „Kommentare" stehen nur, wo die Ansicht diese Arten
 * auch führt (`mitTerminen`, `mitKommentaren`). Ohne den Schalter zählen
 * Kommentare als Notizen, und Termine werden gar nicht erst geladen — ein
 * Chip mit dauerhaft 0 wäre eine Behauptung über etwas, das es in dieser
 * Ansicht nicht gibt.
 */
function chipArten(opt: {
    mitTerminen?: boolean;
    mitKommentaren?: boolean;
    ohneNotizen?: boolean;
}): Array<{ art: ArchivArt | "alle"; label: string }> {
    return [
        ...ARTEN.filter((a) => !(opt.ohneNotizen && a.art === "notiz")),
        ...(opt.mitTerminen ? [{ art: "termin" as const, label: "Termine" }] : []),
        ...(opt.mitKommentaren ? [{ art: "kommentar" as const, label: "Kommentare" }] : []),
    ];
}

/**
 * Auf-/zugeklappt pro Vorgang, wie es die übrigen Redesign-Ansichten halten:
 * `sessionStorage`, best effort. Wer im Archiv sucht, soll es nicht bei jedem
 * Wechsel neu aufklappen.
 */
const offenKey = (vorgangId: number) => `kmvs.vorgang.v4.archiv.${vorgangId}`;

function liesOffen(vorgangId: number, standard: boolean): boolean {
    try {
        const gemerkt = sessionStorage.getItem(offenKey(vorgangId));
        return gemerkt === null ? standard : gemerkt === "1";
    } catch {
        // sessionStorage blockiert (privates Fenster, Richtlinie) — dann eben
        // jedes Mal der Ausgangszustand.
        return standard;
    }
}

function merkeOffen(vorgangId: number, offen: boolean): void {
    try {
        sessionStorage.setItem(offenKey(vorgangId), offen ? "1" : "0");
    } catch {
        /* Merken ist best effort. */
    }
}

type Props = {
    vorgangId: number;
    /** Für den Namen des ZIP beim Herunterladen („Vorgang V-… – Dokumente.zip"). */
    vorgangNbr?: string | null;
    /**
     * ALLE Einträge des Vorgangs — `eintraege`, nicht `eigeneEintraege`. Was
     * an einem To-do hängt, gehört ins Archiv; genau das ist sein Zweck.
     */
    eintraege: VerlaufEintrag[];
    /**
     * Die Notizen aus dem Seiten-State. Sie kommen SEPARAT, weil `baueVerlauf`
     * sie seit PO 08.09.2026 nicht mehr auswertet — siehe `archivZeilen.ts`.
     */
    notizen: AggregatedNoteDto[];
    /** Nur für die Herkunftsangabe an Mail, Anruf und Dokument. */
    todos: Array<{ id: number; titel: string }>;
    kundeName: string | null;
    onOeffneMail: (link: MailLinkDto) => void;
    /**
     * Klick auf eine Termin-Zeile: zur Termin-Notizen-Seite (PO 14.09.2026).
     * Optional wie `mitTerminen` — nur V4 lädt Termine; ohne den Handler ist
     * die Zeile ein Absatz, kein Knopf (dieselbe Regel wie am Anruf).
     */
    onOeffneTermin?: (terminId: number) => void;
    /**
     * Termine als eigene Art führen und als Chip anbieten (PO 14.09.2026:
     * die Termin-Notiz erscheint im Vorgang — und dann muss man sie hier auch
     * finden). Ein Schalter, weil nur der Aufrufer weiß, ob er die Termine
     * geladen hat.
     */
    mitTerminen?: boolean;
    /**
     * Kommentare als eigene Art führen und als fünften Chip anbieten
     * (PO 11.09.2026: „Nur dann muss ich nach dem Ding auch suchen können").
     *
     * V4 setzt das: dort stehen die Kommentare seit dem 14.09.2026 hinter
     * der Sprechblase am Block, und das Archiv ist der Weg, sie über alle
     * To-dos hinweg zu finden.
     */
    mitKommentaren?: boolean;
    /**
     * Notizen weglassen — sie stehen als Text in ihrer Karte (PO 28.09.2026:
     * „Notizen nicht im Archiv"); Kommentare bleiben. Siehe `archivZeilen`.
     */
    ohneNotizen?: boolean;
    /** Die Spalte „aus welchem To-do" — ohne To-dos wäre sie immer leer. */
    mitHerkunft?: boolean;
    /** Rechtsklick auf eine Zeile: Umbenennen, Zuweisen, Entfernen. */
    onEreignisMenue?: (eintrag: VerlaufEintrag, e: React.MouseEvent) => void;
    /** Die Sprechblase an Mail, Anruf und Dokument. */
    kommentare?: ZeilenKommentare;
};

export const ArchivAbschnitt: React.FC<Props> = ({
    vorgangId,
    vorgangNbr,
    eintraege,
    notizen,
    todos,
    kundeName,
    onOeffneMail,
    onOeffneTermin,
    mitTerminen,
    mitKommentaren,
    ohneNotizen,
    mitHerkunft = true,
    onEreignisMenue,
    kommentare,
}) => {
    const [offen, setOffen] = useState<boolean>(() => liesOffen(vorgangId, false));

    const umschalten = useCallback(() => {
        setOffen((vorher) => {
            const nachher = !vorher;
            merkeOffen(vorgangId, nachher);
            return nachher;
        });
    }, [vorgangId]);

    const alle = useMemo(
        () =>
            archivZeilen(
                {
                    eintraege,
                    notizen,
                    todos,
                    ...(mitKommentaren ? { mitKommentaren } : {}),
                    ...(ohneNotizen ? { ohneNotizen } : {}),
                },
                kundeName,
            ),
        [eintraege, notizen, todos, kundeName, mitKommentaren, ohneNotizen],
    );
    const proKey = useMemo(() => new Map(eintraege.map((e) => [e.key, e])), [eintraege]);
    const menue = useMemo(
        () =>
            onEreignisMenue
                ? (z: { key: string }, e: React.MouseEvent) => {
                      const eintrag = proKey.get(z.key);
                      if (eintrag) onEreignisMenue(eintrag, e);
                  }
                : undefined,
        [onEreignisMenue, proKey],
    );

    return (
        <section className="vg4-card vg4-archiv">
            <button
                type="button"
                className="vg4-archiv-kopf"
                aria-expanded={offen}
                onClick={umschalten}
            >
                {/* DER AUFKLAPPER STEHT LINKS — überall gleich (PO 18.09.2026:
                    „Ausklapp-Button immer links, auch bei Archiv"). An der
                    To-do-Karte steht er vor dem Betreff, hier vor dem Wort
                    „Archiv": Wer die Seite überfliegt, findet ihn damit immer
                    an derselben Kante. Er zeigt nach UNTEN — dorthin, wo sich
                    der Abschnitt öffnet. */}
                <span className={`vg4-chev${offen ? " open" : ""}`} aria-hidden>
                    <IcoChevronUnten size={16} />
                </span>
                <span className="vg4-archiv-t">Archiv</span>
                {/* „34 Einträge aus 3 To-dos" — der Erklärsatz „Alles zu diesem
                    Vorgang" ist ersatzlos entfallen: er sagte nichts, was die
                    Zahl nicht schon sagt. */}
                <span className="vg4-archiv-n">{archivKopfText(alle.length, todos.length)}</span>
            </button>

            {offen && (
                <EreignisListeV4
                    zeilen={alle}
                    arten={chipArten({
                        ...(mitTerminen ? { mitTerminen } : {}),
                        ...(mitKommentaren ? { mitKommentaren } : {}),
                        ...(ohneNotizen ? { ohneNotizen } : {}),
                    })}
                    mitHerkunft={mitHerkunft}
                    leerHinweis="An diesem Vorgang hängt noch nichts."
                    suchPlatzhalter="In diesem Vorgang suchen …"
                    onOeffneMail={onOeffneMail}
                    // Herunterladen darf, wer nachschlagen darf (PO 28.09.2026).
                    zipName={zipDateiname({ id: vorgangId, vorgangNbr: vorgangNbr ?? null })}
                    {...(kommentare?.users ? { users: kommentare.users } : {})}
                    {...(onOeffneTermin ? { onOeffneTermin } : {})}
                    {...(menue ? { onKontextmenue: menue } : {})}
                    {...(kommentare ? { kommentare } : {})}
                />
            )}
        </section>
    );
};
